/* istanbul ignore file */

import type { MouseEvent, UIEvent } from 'react';
import React, { useEffect, useRef, useState } from 'react';
import { useWindowSize } from 'react-use';
import { toSentenceCase } from '@noths/polaris-client-utils';

import * as styles from './Breadcrumbs.styles';

const FADE_MIN_THRESHOLD_IN_PX = 10;

export interface BreadcrumbLink {
  rel: string;
  text: string;
}

export interface BreadcrumbLinkClickPayload {
  linkDepth: number;
}

export interface BreadcrumbsProps {
  includesCurrentPage?: boolean;
  links: BreadcrumbLink[];
  onLinkClick?: (
    e: MouseEvent<HTMLAnchorElement>,
    { linkDepth }: BreadcrumbLinkClickPayload,
  ) => void;
}

export const Breadcrumbs = ({
  includesCurrentPage = false,
  links,
  onLinkClick,
}: BreadcrumbsProps) => {
  const { width } = useWindowSize();
  const containerRef = useRef<HTMLOListElement | null>(null);
  const [shouldShowLeftFade, setShouldShowLeftFade] = useState(false);

  const handleOnScroll = ({ target }: UIEvent<HTMLOListElement>) =>
    setShouldShowLeftFade((target as HTMLOListElement).scrollLeft >= FADE_MIN_THRESHOLD_IN_PX);

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = containerRef.current.scrollWidth;
    }
  }, [containerRef, width]);

  const onClick = (e: MouseEvent<HTMLAnchorElement>, index: number) => {
    const linkDepth = includesCurrentPage ? links.length - 1 - index : links.length - index;

    onLinkClick?.(e, { linkDepth });
  };

  return (
    <nav
      aria-label="Breadcrumb"
      css={styles.breadcrumbNav}
      data-left-fade-visible={shouldShowLeftFade}
    >
      <ol css={styles.breadcrumbList} onScroll={handleOnScroll} ref={containerRef}>
        {links.map((link, index) => {
          const isLast = index === links.length - 1;
          const text = toSentenceCase(link.text.toLowerCase());

          return (
            <li css={styles.breadcrumbListItem} key={link.text}>
              {includesCurrentPage && isLast ? (
                <span aria-current="page" css={styles.breadcrumbLink}>
                  {text}
                </span>
              ) : (
                <a css={styles.breadcrumbLink} href={link.rel} onClick={(e) => onClick(e, index)}>
                  {text}
                </a>
              )}
            </li>
          );
        })}
      </ol>
    </nav>
  );
};
